import React from 'react';
import { colors, borderRadius, fontSize, fontWeight, transitions } from '../../constants/theme';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline' | 'warning';
  size?: 'sm' | 'md' | 'lg' | 'small';
  fullWidth?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  icon,
  children,
  className = '',
  disabled,
  ...rest
}) => {
  // sizeのnormalize ('small' -> 'sm')
  const normalizedSize = size === 'small' ? 'sm' : size;
  
  const variantStyles = {
    primary: {
      backgroundColor: colors.accent.blue,
      color: colors.ui.bg,
      border: 'none',
      hover: {
        backgroundColor: '#3A7AD6',
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(77, 143, 234, 0.3)'
      }
    },
    secondary: {
      backgroundColor: colors.ui.bg,
      color: colors.accent.blue,
      border: `2px solid ${colors.accent.blue}`,
      hover: {
        backgroundColor: '#F0F7FF',
        borderColor: '#3A7AD6'
      }
    },
    ghost: {
      backgroundColor: 'transparent',
      color: colors.text.primary,
      border: 'none',
      hover: {
        backgroundColor: colors.ui.bgSecondary
      }
    },
    danger: {
      backgroundColor: colors.accent.red,
      color: colors.ui.bg,
      border: 'none',
      hover: {
        backgroundColor: '#D44A44',
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(232, 92, 86, 0.3)'
      }
    },
    outline: {
      backgroundColor: 'transparent',
      color: colors.text.primary,
      border: `1.5px solid ${colors.ui.border}`,
      hover: {
        backgroundColor: colors.ui.bgSecondary,
        borderColor: colors.text.secondary
      }
    },
    warning: {
      backgroundColor: '#F5A623',
      color: colors.ui.bg,
      border: 'none',
      hover: {
        backgroundColor: '#E09612',
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(245, 166, 35, 0.3)'
      }
    }
  };

  const sizeStyles = {
    sm: {
      padding: '6px 12px',
      fontSize: fontSize.sm,
      borderRadius: borderRadius.sm
    },
    md: {
      padding: '10px 20px',
      fontSize: fontSize.base,
      borderRadius: borderRadius.md
    },
    lg: {
      padding: '14px 28px',
      fontSize: fontSize.lg,
      borderRadius: borderRadius.md
    }
  };

  const baseStyles: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontWeight: fontWeight.medium,
    transition: `all ${transitions.base}`,
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    opacity: disabled || loading ? 0.6 : 1,
    width: fullWidth ? '100%' : 'auto',
    outline: 'none',
    position: 'relative',
    overflow: 'hidden',
    ...(variantStyles[variant] || variantStyles.primary),
    ...sizeStyles[normalizedSize]
  };

  const [isHovered, setIsHovered] = React.useState(false);

  const handleMouseEnter = () => {
    if (!disabled && !loading) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const appliedStyles: React.CSSProperties = {
    ...baseStyles,
    ...(isHovered && !disabled && !loading && variantStyles[variant]?.hover ? variantStyles[variant].hover : {})
  };

  return (
    <button
      className={className}
      style={appliedStyles}
      disabled={disabled || loading}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...rest}
    >
      {loading && (
        <span
          style={{
            display: 'inline-block',
            width: '14px',
            height: '14px',
            border: '2px solid transparent',
            borderTopColor: variant === 'primary' || variant === 'danger' ? colors.ui.bg : colors.accent.blue,
            borderRadius: '50%',
            animation: 'spin 0.6s linear infinite'
          }}
        />
      )}
      {!loading && icon}
      {children}
    </button>
  );
};

export default Button;
export { Button };